import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import type { MathSubject } from '@shared/mathSubjects';

export type FocusTransitionKind = 'level' | 'subject';
export type FocusTransitionPhase = 'idle' | 'saving' | 'loading' | 'error';
export type FocusTransitionErrorStage = 'save' | 'content';

export interface FocusTransitionTarget {
  /** Undefined means this transition did not change the level axis. */
  levelKey?: string | null;
  /** Undefined means this transition did not change the subject axis. */
  mathSubject?: MathSubject | null;
}

interface FocusTransitionState {
  id: number;
  /** One clock spanning save, applicability, navigation, and destination data. */
  startedAt: number | null;
  phase: FocusTransitionPhase;
  kind: FocusTransitionKind | null;
  destinationLabel: string | null;
  target: FocusTransitionTarget;
  error: Error | null;
  errorStage: FocusTransitionErrorStage | null;
}

interface BeginTransitionOptions {
  kind: FocusTransitionKind;
  destinationLabel: string;
  target: FocusTransitionTarget;
  retry: () => void;
}

export interface FocusTransitionContextValue extends FocusTransitionState {
  isSaving: boolean;
  isContentLoading: boolean;
  isBusy: boolean;
  begin: (options: BeginTransitionOptions) => number;
  accept: (id: number) => void;
  failSave: (id: number, error: unknown) => void;
  failContent: (id: number, error: unknown, retry: () => void) => void;
  complete: (id: number) => void;
  retry: () => void;
  dismissError: () => void;
}

const initialState: FocusTransitionState = {
  id: 0,
  startedAt: null,
  phase: 'idle',
  kind: null,
  destinationLabel: null,
  target: {},
  error: null,
  errorStage: null,
};

const inertContext: FocusTransitionContextValue = {
  ...initialState,
  isSaving: false,
  isContentLoading: false,
  isBusy: false,
  begin: () => 0,
  accept: () => undefined,
  failSave: () => undefined,
  failContent: () => undefined,
  complete: () => undefined,
  retry: () => undefined,
  dismissError: () => undefined,
};

const FocusTransitionContext =
  createContext<FocusTransitionContextValue>(inertContext);

const asError = (error: unknown): Error =>
  error instanceof Error
    ? error
    : new Error('The request could not be completed.');

/**
 * Owns the user-scoped focus hand-off across student routes.
 *
 * The focus hooks still own server/cache truth. This provider owns only the
 * transient UX contract that spans those hooks: save the requested setting,
 * wait for destination content, and keep retry/error state alive if the user
 * navigates while either half is in flight. It performs no reads by itself.
 */
export const FocusTransitionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { user } = useAuth0();
  const [state, setState] = useState<FocusTransitionState>(initialState);
  const idRef = useRef(0);
  const retryRef = useRef<(() => void) | null>(null);
  const retryPhaseRef = useRef<'saving' | 'loading'>('saving');

  // A warm app shell survives sign-out/sign-in. Transition state must not.
  useEffect(() => {
    idRef.current += 1;
    retryRef.current = null;
    setState({ ...initialState, id: idRef.current });
  }, [user?.sub]);

  const begin = useCallback((options: BeginTransitionOptions): number => {
    const id = idRef.current + 1;
    idRef.current = id;
    retryRef.current = options.retry;
    retryPhaseRef.current = 'saving';
    setState({
      id,
      startedAt: Date.now(),
      phase: 'saving',
      kind: options.kind,
      destinationLabel: options.destinationLabel,
      target: options.target,
      error: null,
      errorStage: null,
    });
    return id;
  }, []);

  const accept = useCallback((id: number) => {
    setState(previous =>
      previous.id === id
        ? {
            ...previous,
            phase: 'loading',
            error: null,
            errorStage: null,
          }
        : previous
    );
  }, []);

  const failSave = useCallback((id: number, error: unknown) => {
    retryPhaseRef.current = 'saving';
    setState(previous =>
      previous.id === id
        ? {
            ...previous,
            phase: 'error',
            error: asError(error),
            errorStage: 'save',
          }
        : previous
    );
  }, []);

  const failContent = useCallback(
    (id: number, error: unknown, retry: () => void) => {
      retryRef.current = retry;
      retryPhaseRef.current = 'loading';
      setState(previous =>
        previous.id === id
          ? {
              ...previous,
              phase: 'error',
              error: asError(error),
              errorStage: 'content',
            }
          : previous
      );
    },
    []
  );

  const complete = useCallback((id: number) => {
    setState(previous => {
      if (previous.id !== id || previous.phase !== 'loading') return previous;
      retryRef.current = null;
      return { ...initialState, id };
    });
  }, []);

  const retry = useCallback(() => {
    const retryAction = retryRef.current;
    if (!retryAction) return;
    setState(previous => ({
      ...previous,
      phase: retryPhaseRef.current,
      error: null,
      errorStage: null,
    }));
    retryAction();
  }, []);

  const dismissError = useCallback(() => {
    retryRef.current = null;
    setState(previous =>
      previous.phase === 'error'
        ? { ...initialState, id: previous.id }
        : previous
    );
  }, []);

  const value = useMemo<FocusTransitionContextValue>(
    () => ({
      ...state,
      isSaving: state.phase === 'saving',
      isContentLoading: state.phase === 'loading',
      isBusy: state.phase === 'saving' || state.phase === 'loading',
      begin,
      accept,
      failSave,
      failContent,
      complete,
      retry,
      dismissError,
    }),
    [state, begin, accept, failSave, failContent, complete, retry, dismissError]
  );

  return (
    <FocusTransitionContext.Provider value={value}>
      {children}
    </FocusTransitionContext.Provider>
  );
};

export const useFocusTransition = (): FocusTransitionContextValue =>
  useContext(FocusTransitionContext);

export interface FocusIdentity {
  levelKey?: string | null;
  mathSubject?: MathSubject | null;
}

export const focusTransitionMatches = (
  target: FocusTransitionTarget,
  identity: FocusIdentity
): boolean => {
  if (
    target.levelKey !== undefined &&
    target.levelKey !== (identity.levelKey ?? null)
  ) {
    return false;
  }
  if (
    target.mathSubject !== undefined &&
    target.mathSubject !== (identity.mathSubject ?? null)
  ) {
    return false;
  }
  return true;
};
